import React, { useCallback, useEffect, useState, useRef, Fragment } from 'react';
import { ArrowDownIcon, ArrowUpIcon, ArrowRightLeftIcon, StarIcon, MonitorIcon, UsersIcon, ShieldCheckIcon, ClipboardCheckIcon, FileTextIcon, GlobeIcon, TruckIcon, HeadphonesIcon, BarChart3Icon, MessageSquareIcon, CheckCircleIcon, CalendarIcon, PackageIcon, RocketIcon, UserCheckIcon, MonitorSmartphoneIcon } from 'lucide-react';
interface Handoff {
  direction: 'up' | 'down' | 'both';
  target: string;
  label: string;
}
interface StepProps {
  title: string;
  variant: 'cpo' | 'scopt';
  milestone?: boolean;
  icon?: React.ReactNode;
  bullets?: string[];
  options?: string[];
  handoff?: Handoff;
  stepId?: string;
}
function Step({
  title,
  variant,
  milestone,
  icon,
  bullets,
  options,
  handoff,
  stepId
}: StepProps) {
  const base = variant === 'cpo' ? milestone ? 'bg-blue-100 border-blue-500 text-blue-900 ring-2 ring-blue-400/50 ring-offset-1' : 'bg-blue-50 border-blue-200 text-blue-900' : milestone ? 'bg-emerald-100 border-emerald-500 text-emerald-900 ring-2 ring-emerald-400/50 ring-offset-1' : 'bg-emerald-50 border-emerald-200 text-emerald-900';
  return <div data-step-id={stepId} className={`relative rounded-lg border-2 px-3 py-2.5 shadow-sm w-full min-h-[56px] ${base}`} data-id="element-1">
      <div className="flex items-start gap-2" data-id="element-2">
        {icon && <span className="mt-0.5 flex-shrink-0 opacity-70" data-id="element-3">{icon}</span>}
        <div className="min-w-0 flex-1" data-id="element-4">
          <p className="text-xs font-semibold leading-snug" data-id="element-5">{title}</p>
          {bullets && <ul className="mt-1.5 space-y-0.5" data-id="element-6">
              {bullets.map((b, i) => <li key={i} className="flex items-start gap-1.5 text-[10px] leading-snug opacity-75" data-id="element-7">
                  <span className="mt-0.5 flex-shrink-0" data-id="element-8">•</span>
                  <span data-id="element-9">{b}</span>
                </li>)}
            </ul>}
          {options && <div className="mt-2 space-y-1" data-id="element-10">
              {options.map((opt, i) => <Fragment key={i} data-id="element-11">
                  <div className="flex items-center gap-2" data-id="element-12">
                    <span className="flex-shrink-0 inline-flex items-center justify-center h-4 w-4 rounded-full border border-current text-[9px] font-bold leading-none" data-id="element-13">
                      {i + 1}
                    </span>
                    <span className="text-[10px] font-medium leading-snug" data-id="element-14">
                      {opt}
                    </span>
                  </div>
                  {i < options.length - 1 && <div className="flex items-center gap-1.5 pl-0.5" data-id="element-15">
                      <div className="flex-1 h-px bg-current opacity-15" data-id="element-16" />
                      <span className="text-[8px] font-bold uppercase tracking-wider opacity-50" data-id="element-17">
                        or
                      </span>
                      <div className="flex-1 h-px bg-current opacity-15" data-id="element-18" />
                    </div>}
                </Fragment>)}
            </div>}
          {milestone && <div className="mt-1.5 flex items-center gap-1 text-amber-600" data-id="element-19">
              <StarIcon className="h-3 w-3 fill-current" data-id="element-20" />
              <span className="text-[9px] font-bold uppercase tracking-wider" data-id="element-21">
                Milestone
              </span>
            </div>}
          {handoff && <div className={`mt-2 flex items-start gap-1.5 rounded px-2 py-1 text-[9px] font-bold border ${handoff.direction === 'up' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : handoff.direction === 'down' ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-amber-50 border-amber-200 text-amber-700'}`} data-id="element-22">
              <div className="mt-px flex-shrink-0" data-id="element-23">
                {handoff.direction === 'up' && <ArrowUpIcon className="h-2.5 w-2.5" data-id="element-24" />}
                {handoff.direction === 'down' && <ArrowDownIcon className="h-2.5 w-2.5" data-id="element-25" />}
                {handoff.direction === 'both' && <ArrowRightLeftIcon className="h-2.5 w-2.5" data-id="element-26" />}
              </div>
              <span className="leading-snug" data-id="element-27">
                <span className="opacity-60" data-id="element-28">
                  {handoff.direction === 'both' ? 'WITH ' : 'TO '}
                </span>
                {handoff.target}
              </span>
            </div>}
        </div>
      </div>
    </div>;
}
const SLICES = 14;
const LABEL_W = '160px';
const GRID = `${LABEL_W} repeat(${SLICES}, minmax(160px, 1fr))`;
const phaseConfig: {
  name: string;
  num: string;
  span: number;
  note?: string;
}[] = [{
  name: 'Request & Contact',
  num: '1',
  span: 1
}, {
  name: 'Qualification & Contract',
  num: '2',
  span: 3
}, {
  name: 'Setup & Alignment',
  num: '3',
  span: 2
}, {
  name: 'Technical Configuration',
  num: '4',
  span: 2
}, {
  name: 'Handover & Launch',
  num: '5',
  span: 2
}, {
  name: 'Trial Period',
  num: '6',
  span: 3,
  note: '1 Month'
}, {
  name: 'Evaluation',
  num: '7',
  span: 1
}];
/* Shared sticky label styles */
const stickyLabel = 'sticky left-0 z-20';
function Cell({
  children,
  bg = 'bg-white/80',
  border = true,
  className = ''
}: {
  children?: React.ReactNode;
  bg?: string;
  border?: boolean;
  className?: string;
}) {
  return <div className={`${bg} ${border ? 'border-r border-gray-100' : ''} px-2 py-2.5 flex items-center justify-center ${className}`} data-id="element-29">
      {children}
    </div>;
}
function Empty({
  bg = 'bg-white/80',
  border = true,
  className = ''
}: {
  bg?: string;
  border?: boolean;
  className?: string;
}) {
  return <div className={`${bg} ${border ? 'border-r border-gray-100' : ''} p-1 ${className}`} data-id="element-30" />;
}
/* ── Arrow connection definitions ── */
interface ArrowConnection {
  from: string;
  to: string;
  type: 'vertical' | 'horizontal';
}
const CONNECTIONS: ArrowConnection[] = [{
  from: 'free-trial-request',
  to: 'receives-request',
  type: 'vertical'
}, {
  from: 'receives-request',
  to: 'verify-align',
  type: 'horizontal'
}, {
  from: 'verify-align',
  to: 'contract-signed',
  type: 'horizontal'
}, {
  from: 'contract-signed',
  to: 'confirmation-trial',
  type: 'horizontal'
}, {
  from: 'confirmation-trial',
  to: 'kick-off',
  type: 'horizontal'
}, {
  from: 'kick-off',
  to: 'provide-setup-info',
  type: 'horizontal'
}, {
  from: 'provide-setup-info',
  to: 'set-up',
  type: 'horizontal'
}, {
  from: 'set-up',
  to: 'activate-dns',
  type: 'horizontal'
}, {
  from: 'activate-dns',
  to: 'launch',
  type: 'horizontal'
}, {
  from: 'launch',
  to: 'ship-dongles',
  type: 'horizontal'
}, {
  from: 'ship-dongles',
  to: 'deliver-dongles',
  type: 'horizontal'
}, {
  from: 'deliver-dongles',
  to: 'onboarding',
  type: 'horizontal'
}, {
  from: 'onboarding',
  to: 'platform-usage',
  type: 'horizontal'
}, {
  from: 'platform-usage',
  to: 'support-trial',
  type: 'vertical'
}, {
  from: 'support-trial',
  to: 'eval-kpi-cs',
  type: 'horizontal'
}];
interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}
type StepPositions = Record<string, Rect>;
function useStepPositions(containerRef: React.RefObject<HTMLDivElement | null>) {
  const [positions, setPositions] = useState<StepPositions>({});
  const measure = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const containerRect = container.getBoundingClientRect();
    const els = container.querySelectorAll<HTMLElement>('[data-step-id]');
    const next: StepPositions = {};
    els.forEach(el => {
      const id = el.getAttribute('data-step-id');
      if (!id) return;
      const r = el.getBoundingClientRect();
      next[id] = {
        top: r.top - containerRect.top,
        left: r.left - containerRect.left,
        width: r.width,
        height: r.height
      };
    });
    setPositions(next);
  }, [containerRef]);
  useEffect(() => {
    measure();
    const container = containerRef.current;
    if (!container) return;
    const ro = new ResizeObserver(() => measure());
    ro.observe(container);
    const handleResize = () => measure();
    window.addEventListener('resize', handleResize);
    const timer = setTimeout(measure, 500);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', handleResize);
      clearTimeout(timer);
    };
  }, [measure, containerRef]);
  return positions;
}
function ArrowOverlay({
  positions,
  connections,
  containerHeight,
  containerWidth
}: {
  positions: StepPositions;
  connections: ArrowConnection[];
  containerHeight: number;
  containerWidth: number;
}) {
  if (Object.keys(positions).length === 0) return null;
  const paths: React.ReactNode[] = [];
  connections.forEach((conn, i) => {
    const from = positions[conn.from];
    const to = positions[conn.to];
    if (!from || !to) return;
    let startX: number, startY: number, endX: number, endY: number;
    let d: string;
    if (conn.type === 'vertical') {
      const goingDown = from.top < to.top;
      if (goingDown) {
        startX = from.left + from.width / 2;
        startY = from.top + from.height;
        endX = to.left + to.width / 2;
        endY = to.top;
      } else {
        startX = from.left + from.width / 2;
        startY = from.top;
        endX = to.left + to.width / 2;
        endY = to.top + to.height;
      }
      const midY = (startY + endY) / 2;
      if (Math.abs(startX - endX) < 2) {
        d = `M ${startX} ${startY} L ${endX} ${endY}`;
      } else {
        d = `M ${startX} ${startY} C ${startX} ${midY}, ${endX} ${midY}, ${endX} ${endY}`;
      }
    } else {
      startX = from.left + from.width;
      startY = from.top + from.height / 2;
      endX = to.left;
      endY = to.top + to.height / 2;
      const dx = endX - startX;
      const cpOffset = Math.max(Math.abs(dx) * 0.4, 20);
      d = `M ${startX} ${startY} C ${startX + cpOffset} ${startY}, ${endX - cpOffset} ${endY}, ${endX} ${endY}`;
    }
    paths.push(<path key={`path-${i}`} d={d} fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeOpacity="0.7" data-id="element-31" />);
  });
  return <svg className="absolute top-0 left-0 w-full h-full" style={{
    pointerEvents: 'none',
    zIndex: 10
  }} width={containerWidth} height={containerHeight} viewBox={`0 0 ${containerWidth} ${containerHeight}`} preserveAspectRatio="none" data-id="element-32">
      {paths}
    </svg>;
}
export function SwimLaneDiagram() {
  const containerRef = useRef<HTMLDivElement>(null);
  const positions = useStepPositions(containerRef);
  const [dims, setDims] = useState({
    w: 0,
    h: 0
  });
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const updateDims = () => setDims({
      w: el.scrollWidth,
      h: el.scrollHeight
    });
    updateDims();
    const ro = new ResizeObserver(updateDims);
    ro.observe(el);
    const timer = setTimeout(updateDims, 500);
    return () => {
      ro.disconnect();
      clearTimeout(timer);
    };
  }, []);
  const bb = 'border-b-2 border-b-blue-300';
  const be = 'border-b-2 border-b-emerald-300';
  return <div className="w-full min-h-screen bg-gray-50 p-4 md:p-8" data-id="element-33">
      <div className="mb-6" data-id="element-34">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight" data-id="element-35">
          Free Trial Onboarding Process
        </h1>
        <p className="mt-1 text-sm text-gray-500" data-id="element-36">
          Swimming lane diagram — CPO (Customer) ↔ Scopt (Provider)
        </p>
      </div>

      <div ref={containerRef} className="overflow-x-auto pb-4" data-id="element-37">
        <div className="relative bg-white rounded-xl shadow-sm border border-gray-200" style={{
        minWidth: `${160 + SLICES * 170}px`
      }} data-id="element-38">
          {/* SVG Arrow Overlay */}
          <ArrowOverlay positions={positions} connections={CONNECTIONS} containerHeight={dims.h} containerWidth={dims.w} data-id="element-39" />

          {/* Phase Headers */}
          <div className="grid gap-0 bg-gray-50" style={{
          gridTemplateColumns: GRID
        }} data-id="element-40">
            <div className={`p-2 border-b-2 border-gray-200 bg-gray-50 ${stickyLabel}`} data-id="element-41" />
            {phaseConfig.map(p => <div key={p.num} className="px-2 py-3 text-center border-b-2 border-gray-200 border-l-2 border-l-gray-300" style={{
            gridColumn: `span ${p.span}`
          }} data-id="element-42">
                <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-gray-800 text-white text-[10px] font-bold mb-1" data-id="element-43">
                  {p.num}
                </span>
                <p className="text-[11px] font-bold text-gray-700 uppercase tracking-wide" data-id="element-44">
                  {p.name}
                </p>
                {p.note && <p className="text-[10px] text-gray-500 font-medium mt-0.5" data-id="element-45">
                    {p.note}
                  </p>}
              </div>)}
          </div>

          {/* ═══ CPO ORGANIZATION ═══ */}
          <div className="bg-blue-600 relative" data-id="element-46">
            <div className={`text-white px-3 py-2 font-bold text-sm tracking-wide uppercase flex items-center gap-2 sticky left-0 w-fit`} data-id="element-47">
              <UsersIcon className="h-4 w-4" data-id="element-48" /> CPO — Customer Organization
            </div>
          </div>

          {/* Lane: CPO IT Team */}
          <div className="grid gap-0 items-stretch" style={{
          gridTemplateColumns: GRID
        }} data-id="element-49">
            <div className={`bg-blue-50/70 border-r-2 border-blue-200 px-3 py-2.5 flex items-center ${stickyLabel}`} data-id="element-50">
              <span className="text-[11px] font-bold text-blue-800 uppercase tracking-wide" data-id="element-51">
                IT Team
              </span>
            </div>
            <Empty data-id="element-52" />
            <Empty data-id="element-53" />
            <Empty data-id="element-54" />
            <Empty data-id="element-55" />
            <Empty data-id="element-56" />
            <Empty data-id="element-57" />
            <Empty data-id="element-58" />
            {/* P4b: Activate & Configure DNS */}
            <Cell data-id="element-59">
              <Step stepId="activate-dns" title="Activate & Configure DNS (Two Step Process)" variant="cpo" icon={<GlobeIcon className="h-3.5 w-3.5" data-id="element-61" />} handoff={{
              direction: 'both',
              target: 'Scopt CS',
              label: 'DNS Ping-Pong'
            }} data-id="element-60" />
            </Cell>
            <Empty data-id="element-62" />
            <Empty data-id="element-63" />
            <Empty data-id="element-64" />
            <Empty data-id="element-65" />
            <Empty data-id="element-66" />
            <Empty border={false} data-id="element-67" />
          </div>

          {/* Lane: CPO Operations */}
          <div className="grid gap-0 items-stretch border-t border-gray-100" style={{
          gridTemplateColumns: GRID
        }} data-id="element-68">
            <div className={`bg-blue-50 border-r-2 border-blue-200 px-3 py-2.5 flex items-center ${stickyLabel}`} data-id="element-69">
              <span className="text-[11px] font-bold text-blue-800 uppercase tracking-wide" data-id="element-70">
                Operations
              </span>
            </div>
            <Empty bg="bg-blue-50/20" data-id="element-71" />
            <Empty bg="bg-blue-50/20" data-id="element-72" />
            <Empty bg="bg-blue-50/20" data-id="element-73" />
            <Empty bg="bg-blue-50/20" data-id="element-74" />
            <Empty bg="bg-blue-50/20" data-id="element-75" />
            {/* P3b: Provide Setup Information */}
            <Cell bg="bg-blue-50/20" data-id="element-76">
              <Step stepId="provide-setup-info" title="Provide Setup Information" variant="cpo" icon={<FileTextIcon className="h-3.5 w-3.5" data-id="element-78" />} bullets={['Brandbook', 'CPMS Access', 'Domains']} handoff={{
              direction: 'down',
              target: 'Scopt CS',
              label: 'Assets'
            }} data-id="element-77" />
            </Cell>
            <Empty bg="bg-blue-50/20" data-id="element-79" />
            <Empty bg="bg-blue-50/20" data-id="element-80" />
            <Empty bg="bg-blue-50/20" data-id="element-81" />
            <Empty bg="bg-blue-50/20" data-id="element-82" />
            {/* P6a: Deliver Dongles to End Users */}
            <Cell bg="bg-blue-50/20" data-id="element-83">
              <Step stepId="deliver-dongles" title="Deliver Dongles to End Users" variant="cpo" icon={<PackageIcon className="h-3.5 w-3.5" data-id="element-85" />} options={['Ship to End User', 'Installer']} data-id="element-84" />
            </Cell>
            {/* P6b: Onboarding */}
            <Cell bg="bg-blue-50/20" data-id="element-86">
              <Step stepId="onboarding" title="Onboarding" variant="cpo" icon={<UserCheckIcon className="h-3.5 w-3.5" data-id="element-88" />} options={['Self Install', 'Installer']} data-id="element-87" />
            </Cell>
            {/* P6c: Platform Usage */}
            <Cell bg="bg-blue-50/20" data-id="element-89">
              <Step stepId="platform-usage" title="Platform Usage" variant="cpo" icon={<MonitorSmartphoneIcon className="h-3.5 w-3.5" data-id="element-91" />} bullets={['Feedback Gathering']} data-id="element-90" />
            </Cell>
            <Empty bg="bg-blue-50/20" border={false} data-id="element-92" />
          </div>

          {/* Lane: CPO Decision Maker */}
          <div className="grid gap-0 items-stretch border-t border-gray-100" style={{
          gridTemplateColumns: GRID
        }} data-id="element-93">
            <div className={`bg-blue-50/70 border-r-2 border-blue-200 px-3 py-2.5 flex items-center ${stickyLabel} ${bb}`} data-id="element-94">
              <span className="text-[11px] font-bold text-blue-800 uppercase tracking-wide leading-tight" data-id="element-95">
                Decision Maker
              </span>
            </div>
            <Cell className={bb} data-id="element-96">
              <Step stepId="free-trial-request" title="Free Trial Request" variant="cpo" icon={<MonitorIcon className="h-3.5 w-3.5" data-id="element-98" />} handoff={{
              direction: 'down',
              target: 'Scopt Sales',
              label: 'Form'
            }} data-id="element-97" />
            </Cell>
            <Empty className={bb} data-id="element-99" />
            <Cell className={bb} data-id="element-100">
              <Step stepId="contract-signed" title="Contract Signed" variant="cpo" icon={<FileTextIcon className="h-3.5 w-3.5" data-id="element-102" />} handoff={{
              direction: 'down',
              target: 'Scopt Sales',
              label: 'Contract'
            }} data-id="element-101" />
            </Cell>
            <Empty className={bb} data-id="element-103" />
            <Empty className={bb} data-id="element-104" />
            <Empty className={bb} data-id="element-105" />
            <Empty className={bb} data-id="element-106" />
            <Empty className={bb} data-id="element-107" />
            <Empty className={bb} data-id="element-108" />
            <Empty className={bb} data-id="element-109" />
            <Empty className={bb} data-id="element-110" />
            <Empty className={bb} data-id="element-111" />
            <Empty className={bb} data-id="element-112" />
            <Cell className={bb} border={false} data-id="element-113">
              <Step stepId="eval-kpi-cpo" title="Evaluate KPIs" variant="cpo" milestone icon={<BarChart3Icon className="h-3.5 w-3.5" data-id="element-115" />} handoff={{
              direction: 'both',
              target: 'Scopt',
              label: 'Joint Review'
            }} data-id="element-114" />
            </Cell>
          </div>

          {/* ═══ BOUNDARY ═══ */}
          <div className="grid gap-0 items-stretch bg-gray-100/80 border-y-2 border-dashed border-gray-300" style={{
          gridTemplateColumns: GRID
        }} data-id="element-116">
            <div className={`px-3 py-2 flex items-center justify-end border-r-2 border-gray-300 bg-gray-200/50 ${stickyLabel}`} data-id="element-117">
              <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest text-right leading-tight" data-id="element-118">
                Cross-Org
                <br data-id="element-119" />
                Boundary
              </span>
            </div>
            {Array.from({
            length: SLICES
          }).map((_, i) => <div key={i} className={`p-0.5 ${i < SLICES - 1 ? 'border-r border-gray-200' : ''}`} data-id="element-120" />)}
          </div>

          {/* ═══ SCOPT ORGANIZATION ═══ */}
          <div className="bg-emerald-600 relative" data-id="element-121">
            <div className={`text-white px-3 py-2 font-bold text-sm tracking-wide uppercase flex items-center gap-2 sticky left-0 w-fit`} data-id="element-122">
              <ShieldCheckIcon className="h-4 w-4" data-id="element-123" /> Scopt — Service Provider
            </div>
          </div>

          {/* Lane: Scopt Sales */}
          <div className="grid gap-0 items-stretch" style={{
          gridTemplateColumns: GRID
        }} data-id="element-124">
            <div className={`bg-emerald-50 border-r-2 border-emerald-200 px-3 py-2.5 flex items-center ${stickyLabel}`} data-id="element-125">
              <span className="text-[11px] font-bold text-emerald-800 uppercase tracking-wide" data-id="element-126">
                Sales
              </span>
            </div>
            <Cell bg="bg-emerald-50/20" data-id="element-127">
              <Step stepId="receives-request" title="Receives Request, Contacts Customer & Verifies Alignment" variant="scopt" icon={<MessageSquareIcon className="h-3.5 w-3.5" data-id="element-129" />} bullets={['Customer Alignment', 'CPO Criteria']} data-id="element-128" />
            </Cell>
            <Empty bg="bg-emerald-50/20" data-id="element-130" />
            <Empty bg="bg-emerald-50/20" data-id="element-131" />
            <Cell bg="bg-emerald-50/20" data-id="element-132">
              <Step stepId="confirmation-trial" title="Confirmation of Trial" variant="scopt" milestone icon={<StarIcon className="h-3.5 w-3.5" data-id="element-134" />} data-id="element-133" />
            </Cell>
            <Empty bg="bg-emerald-50/20" data-id="element-135" />
            <Empty bg="bg-emerald-50/20" data-id="element-136" />
            <Empty bg="bg-emerald-50/20" data-id="element-137" />
            <Empty bg="bg-emerald-50/20" data-id="element-138" />
            <Empty bg="bg-emerald-50/20" data-id="element-139" />
            <Empty bg="bg-emerald-50/20" data-id="element-140" />
            <Empty bg="bg-emerald-50/20" data-id="element-141" />
            <Empty bg="bg-emerald-50/20" data-id="element-142" />
            <Empty bg="bg-emerald-50/20" data-id="element-143" />
            <Cell bg="bg-emerald-50/20" border={false} data-id="element-144">
              <Step stepId="eval-kpi-sales" title="Evaluate KPIs" variant="scopt" milestone icon={<BarChart3Icon className="h-3.5 w-3.5" data-id="element-146" />} handoff={{
              direction: 'both',
              target: 'CPO Decision Maker',
              label: 'Joint Review'
            }} data-id="element-145" />
            </Cell>
          </div>

          {/* Lane: Scopt Customer Success */}
          <div className="grid gap-0 items-stretch border-t border-gray-100" style={{
          gridTemplateColumns: GRID
        }} data-id="element-147">
            <div className={`bg-emerald-50/70 border-r-2 border-emerald-200 px-3 py-2.5 flex items-center ${stickyLabel} ${be}`} data-id="element-148">
              <span className="text-[11px] font-bold text-emerald-800 uppercase tracking-wide leading-tight" data-id="element-149">
                Customer Success
              </span>
            </div>
            <Empty className={be} data-id="element-150" />
            <Cell className={be} data-id="element-151">
              <Step stepId="verify-align" title="Verify & Align Expectations" variant="scopt" icon={<ClipboardCheckIcon className="h-3.5 w-3.5" data-id="element-153" />} bullets={['CPO Verification', 'Is Hardware Supported?', 'Prerequisites', 'Trial Timeline', 'KPIs Defined', 'Decision Makers']} data-id="element-152" />
            </Cell>
            <Empty className={be} data-id="element-154" />
            <Empty className={be} data-id="element-155" />
            <Cell className={be} data-id="element-156">
              <Step stepId="kick-off" title="Kick Off Meeting" variant="scopt" icon={<CalendarIcon className="h-3.5 w-3.5" data-id="element-158" />} handoff={{
              direction: 'both',
              target: 'CPO',
              label: 'Joint Meeting'
            }} data-id="element-157" />
            </Cell>
            <Empty className={be} data-id="element-159" />
            <Cell className={be} data-id="element-160">
              <Step stepId="set-up" title="Set Up" variant="scopt" icon={<RocketIcon className="h-3.5 w-3.5" data-id="element-162" />} bullets={['CPO Management Portal', 'Branded User App', 'Deliver DNS Records']} handoff={{
              direction: 'up',
              target: 'CPO IT',
              label: 'DNS Records'
            }} data-id="element-161" />
            </Cell>
            <Empty className={be} data-id="element-163" />
            <Cell className={be} data-id="element-164">
              <Step stepId="launch" title="Launch" variant="scopt" milestone icon={<CheckCircleIcon className="h-3.5 w-3.5" data-id="element-166" />} bullets={['Send Credentials', 'Training']} handoff={{
              direction: 'up',
              target: 'CPO Team',
              label: 'Credentials & Docs'
            }} data-id="element-165" />
            </Cell>
            <Cell className={be} data-id="element-167">
              <Step stepId="ship-dongles" title="Ship Dongles & Instructions to CPO" variant="scopt" icon={<TruckIcon className="h-3.5 w-3.5" data-id="element-169" />} handoff={{
              direction: 'up',
              target: 'CPO Operations',
              label: 'Hardware'
            }} data-id="element-168" />
            </Cell>
            <Empty className={be} data-id="element-170" />
            <Empty className={be} data-id="element-171" />
            <Cell className={be} data-id="element-172">
              <Step stepId="support-trial" title="Support CPO During Trial" variant="scopt" icon={<HeadphonesIcon className="h-3.5 w-3.5" data-id="element-174" />} handoff={{
              direction: 'both',
              target: 'CPO Operations',
              label: 'Support'
            }} data-id="element-173" />
            </Cell>
            <Cell className={be} border={false} data-id="element-175">
              <Step stepId="eval-kpi-cs" title="Evaluate KPIs" variant="scopt" milestone icon={<BarChart3Icon className="h-3.5 w-3.5" data-id="element-177" />} handoff={{
              direction: 'both',
              target: 'CPO Decision Maker',
              label: 'Joint Review'
            }} data-id="element-176" />
            </Cell>
          </div>
        </div>
      </div>
    </div>;
}